import { useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useSignup } from '@biasafe.queries/auth-signup/auth-signup';
import { useVerifyEmail } from '@biasafe.queries/auth-verify-email/auth-verify-email';
import { useResendVerification } from '@biasafe.queries/auth-resend-verification/auth-resend-verification';
import { useCancelVerification } from '@biasafe.queries/auth-cancel-verification/auth-cancel-verification';
import { microsoftSignup } from '@biasafe.queries/auth-microsoft-signup/auth-microsoft-signup';
import { googleSignupConfig } from '@biasafe.queries/auth-google-signup-config/auth-google-signup-config';
import { microsoftSignupConfig } from '@biasafe.queries/auth-microsoft-signup-config/auth-microsoft-signup-config';
import { microsoftSignupNonce } from '@biasafe.queries/auth-microsoft-signup-nonce/auth-microsoft-signup-nonce';
import { TotpMultiFactorGenerator } from 'firebase/auth';
import {
  MICROSOFT_UNLINKED_FLAG,
  markSignInCompleted,
} from '../sessionNotices';
import { exchangeAccessSession } from '../accessSession';
import { accessPage, currentAudience } from '../accessState';
import { useAuthPrefillStore } from './useAuthPrefillStore';
import { usePendingSignupStore } from './usePendingSignupStore';
import type { AccessAudience } from '../accessState';
import type { AccessSessionResponse } from '@biasafe.models';
import { useUserStore } from '@/entities/user/store';
import {
  firebaseAuthMessage,
  firebaseErrorCode,
  isCredentialRejection,
  mfaChallengeError,
} from '@/features/Auth/firebaseAuthMessage';
import {
  extractErrorCode,
  extractErrorData,
  extractErrorMessage,
} from '@/shared/utils/apiErrors';

import { usePortalSessionStore } from '@/features/ClientPortal/portalSession';

const MFA_REQUIRED_CODE = 'auth/multi-factor-auth-required';
const ADMIN_RESTRICTED_CODE = 'auth/admin-restricted-operation';
/** `APIError.ACCOUNT_ALREADY_EXISTS`, the 409 the backend sign-up answers with. */
const ACCOUNT_ALREADY_EXISTS_CODE = 'AU409AAE';

/** One message for "wrong password" and "no such account": they are the same. */
const NEUTRAL_LOGIN_ERROR =
  'Check your email and password, or create an account.';
const ACCOUNT_EXISTS_NOTICE =
  'This address already has an account. Log in to continue.';

type SsoProviderName = 'Google' | 'Microsoft';

/**
 * The Entra / Google client id when the provider's server-side sign-up is on,
 * otherwise null. A failed request reads as off, like the sign-up buttons did,
 * so the person gets the "ask for an invitation" message instead of a step the
 * server would refuse.
 */
async function signupClientId(
  provider: SsoProviderName,
): Promise<string | null> {
  try {
    const cfg =
      provider === 'Google'
        ? await googleSignupConfig()
        : await microsoftSignupConfig();
    return cfg.enabled === true && cfg.client_id ? cfg.client_id : null;
  } catch {
    return null;
  }
}

/**
 * Leave the Firebase error code in the console when SSO fails.
 *
 * The code is what tells us which of the many popup failures someone hit when
 * they report it. Only the code is logged: the error object also carries the
 * email address and, for some codes, the provider credential, and neither
 * belongs in a console someone may screenshot.
 */
function reportSsoFailure(provider: 'Google' | 'Microsoft', err: unknown) {
  // A second factor being required is a normal step of signing in, not a
  // failure. It is handled before this is reached; this keeps the console
  // honest if a caller ever forgets.
  if (firebaseErrorCode(err) === MFA_REQUIRED_CODE) return;
  console.warn(
    `${provider} sign-in failed: ${firebaseErrorCode(err) ?? 'no Firebase code'}`,
  );
}

interface LoginPayload {
  email: string;
  password: string;
}

interface SignUpPayload {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

/**
 * Which second factor a held-back sign-in should challenge for.
 *
 * Extracted and exported because getting it wrong LOCKS ACCOUNTS OUT, and that
 * deserves a test rather than a comment. The branch this replaced hardcoded
 * `phone` on the reasoning that phone was the only Firebase-native factor
 * enrolled. With TOTP available that is false: a TOTP hint labelled `phone`
 * sends the user to a screen that waits for an SMS which will never be sent,
 * for a factor that has no delivery channel at all. The account becomes
 * unreachable — for the user who followed our own advice and picked the
 * strongest method.
 *
 * Prefers TOTP when an account has several. Its code is already on the device,
 * so it is the one that cannot fail to arrive.
 */
export function pickMfaFactor(
  hints: ReadonlyArray<{
    uid: string;
    factorId: string;
    displayName?: string | null;
    /** Present on a phone hint. Identity Platform obfuscates it. */
    phoneNumber?: string | null;
  }>,
): {
  kind: 'phone' | 'totp';
  factorUid: string;
  label?: string;
  target?: string;
} | null {
  if (hints.length === 0) return null;
  const totpId = TotpMultiFactorGenerator.FACTOR_ID;
  const hint = hints.find((h) => h.factorId === totpId) ?? hints[0];
  return {
    kind: hint.factorId === totpId ? 'totp' : 'phone',
    factorUid: hint.uid,
    label: hint.displayName ?? undefined,
    // What the code went to, for the screen to name. Nothing is derived from
    // it — the resolver already knows which factor this is.
    target: hint.phoneNumber ?? undefined,
  };
}

/**
 * The factors OTHER than the one currently shown, so a person who cannot use
 * it (new phone, no signal, authenticator wiped) can switch instead of being
 * stranded. Same ordering as `pickMfaFactor`: TOTP first.
 */
export function alternativeMfaFactors(
  hints: ReadonlyArray<{
    uid: string;
    factorId: string;
    displayName?: string | null;
    phoneNumber?: string | null;
  }>,
  currentUid: string,
): Array<NonNullable<ReturnType<typeof pickMfaFactor>>> {
  const totpId = TotpMultiFactorGenerator.FACTOR_ID;
  return [...hints]
    .filter((h) => h.uid !== currentUid)
    .sort(
      (a, b) => Number(b.factorId === totpId) - Number(a.factorId === totpId),
    )
    .map((h) => pickMfaFactor([h])!);
}

export function useAuthActions({
  audience = currentAudience(),
}: { audience?: AccessAudience } = {}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setAuthenticated = useUserStore((state) => state.setAuthenticated);
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [signupSuccess, setSignupSuccess] = useState(false);
  // True while the verify request in flight was started automatically.
  const silentVerifyRef = useRef(false);
  const [gcpPending, setGcpPending] = useState(false);
  // Set when a Google or Microsoft sign-in found no account for the address and
  // that provider's server-side sign-up is on: the page shows "Create your
  // account" (terms and one button) instead of an error.
  const [ssoCreate, setSsoCreate] = useState<{
    provider: SsoProviderName;
    clientId: string;
  } | null>(null);
  // The last password sign-in was refused with the merged "wrong password or no
  // such account" code. The page renders its message with a create-account link.
  const [credentialsRejected, setCredentialsRejected] = useState(false);
  // False only when the backend reports the confirmation email did not send.
  const [emailDelivered, setEmailDelivered] = useState(true);
  // Set when a correct password still needs a second factor. Holds Firebase's
  // single-use resolver, so it lives in component state and is NEVER persisted
  // — a resolver that outlived the page would be a half-finished sign-in
  // sitting in storage.
  const [mfaChallenge, setMfaChallenge] = useState<{
    /** 'phone' — Firebase-native. 'email' — enforced by our backend. */
    kind: 'phone' | 'email' | 'totp';
    /** Firebase resolver; absent for the email factor, which never reaches one. */
    resolver?: unknown;
    factorUid?: string;
    /** Set once the SMS has actually been sent. */
    verificationId?: string;
    /** The email factor re-posts this token to /auth/mfa/email/challenge. */
    idToken?: string;
    /** Server-reported code lifetime, so the screen never states its own. */
    ttlMinutes?: number;
    label?: string;
    /**
     * Where the code went — a phone number or an email address — for the
     * screen to name. Masked at render, not here: this is the raw value and
     * the masking rule belongs with the copy that shows it.
     */
    target?: string;
    /** Other enrolled Firebase factors the person could use instead. */
    alternatives?: Array<{
      kind: 'phone' | 'totp';
      factorUid: string;
      label?: string;
      target?: string;
    }>;
    /** The SMS send failed, so the resend cooldown must not hold the button. */
    smsFailed?: boolean;
  } | null>(null);
  // The SMS send for a challenge is fired by an effect in the screen that
  // re-runs on every render while no code has been sent. Without these two
  // guards a failed send re-triggered itself on the render its own error
  // caused — one SMS request per render until Firebase said too-many-requests —
  // and a second render during a slow send produced a second text.
  const smsAutoSent = useRef(false);
  const smsInFlight = useRef(false);
  // Stash the signup credentials in a global (in-memory) zustand store
  // so they survive the SignUpPage → VerifyEmailPage navigation.
  // Component-local useState wouldn't work: signupMutation.onSuccess
  // navigates to /verify-email which mounts a fresh useAuthActions
  // instance, blowing away any local creds we'd stashed in SignUpPage.
  // The store is NOT persisted — password lives in JS memory only and
  // gets cleared the moment auto-login fires.
  const pendingSignupCreds = usePendingSignupStore((s) => s.creds);
  const setPendingSignupCreds = usePendingSignupStore((s) => s.setCreds);
  const clearPendingSignupCreds = usePendingSignupStore((s) => s.clearCreds);

  // --- Login ---

  // --- Signup ---
  const signupMutation = useSignup({
    mutation: {
      onSuccess: (data: any) => {
        const id = data?.userId;
        // The account is created either way, but the backend now tells us
        // whether the confirmation email actually went out. Saying "check your
        // email" when nothing was sent leaves the user waiting forever — which
        // is precisely how a silently-disabled SMTP config went unnoticed.
        setEmailDelivered(data?.emailDelivered !== false);
        if (id) {
          setUserId(id);
          navigate({
            to: '/verify-email',
            search: { userId: id },
            replace: true,
          });
        }
        setSignupSuccess(true);
      },
      onError: (err: any, variables: any) => {
        // This address already has an account. The backend has always said so
        // (409 ACCOUNT_ALREADY_EXISTS), so moving the person to the log-in form
        // with the address filled in reveals nothing new. It replaces a dead
        // end: an error on a form they cannot succeed with.
        if (extractErrorCode(err) === ACCOUNT_ALREADY_EXISTS_CODE) {
          clearPendingSignupCreds();
          useAuthPrefillStore.getState().set({
            email: variables?.data?.email ?? '',
            notice: ACCOUNT_EXISTS_NOTICE,
          });
          setError(null);
          navigate({ to: '/login', replace: true });
          return;
        }
        setError(extractErrorMessage(err, 'Sign up failed. Please try again.'));
      },
    },
  });

  // --- Verify Email ---
  const verifyMutation = useVerifyEmail({
    mutation: {
      onSuccess: (data: any) => {
        // An account made with "Sign up with Microsoft": the server has just
        // removed its Microsoft link (see release_pending_microsoft_signup), so
        // there is no password and nothing to auto-login with. Say how to sign
        // in. The onboarding flag below is still set for the first login.
        const microsoftUnlinked = data?.microsoftUnlinked === 'true';
        // Flag the next login as a fresh-signup so the login mutation
        // routes through /onboarding instead of /strategies.  Setting
        // this BEFORE firing the auto-login (or before redirecting to
        // /login on the fallback path) means whichever route the user
        // ultimately ends up logging in from, the onSuccess handler
        // sees the flag and routes to /onboarding.
        if (typeof window !== 'undefined') {
          window.sessionStorage.setItem('biasafe_onboarding_pending', '1');
        }
        // If we still have the credentials the user typed at signup,
        // auto-login now so they skip the manual /login bounce.
        //
        // This MUST go through `login()`, not a direct API mutation: the
        // account exists only in Identity Platform. Calling the old endpoint
        // here meant the step immediately after "email confirmed" answered
        //   404 {"code":"AU404ANF","message":"No account found for this email."}
        // for an account that had just been created and verified. No session
        // cookie was ever minted, so every protected request that followed
        // 401'd, the interceptor logged the user out, and the client-side
        // draft stores were wiped along with it.
        //
        // `login()` does the whole dance: Firebase sign-in ->
        // /auth/gcp/session (-> the email second factor when enrolled).
        if (microsoftUnlinked) {
          navigate({
            to: '/login',
            search: { [MICROSOFT_UNLINKED_FLAG]: '1' },
            replace: true,
          } as any);
          return;
        }
        if (pendingSignupCreds) {
          login(pendingSignupCreds);
          return;
        }
        // Fallback: no stashed creds (the user opened the verify-email
        // link in a different tab, cleared storage, etc.). Send them
        // to /login — the onboarding-pending flag stays set in
        // sessionStorage so loginMutation.onSuccess will still route
        // them to /onboarding once they type their password.
        navigate({ to: '/login', replace: true });
      },
      onError: (err: any) => {
        // An automatic re-check (tab regained focus, other tab announced the
        // link) failing means "not verified yet", which is the normal state
        // while the user is still reading their inbox. Showing "Verification
        // failed" there would be an error nobody caused. Only the button's own
        // click reports one.
        if (silentVerifyRef.current) return;
        setError(
          extractErrorMessage(err, 'Verification failed. Please try again.'),
        );
      },
    },
  });

  /**
   * Where a completed sign-in lands.
   *
   * A user who just finished the signup flow has to reach /onboarding, not
   * /dashboard. This used to hardcode /dashboard, so a fresh signup landed on
   * a dashboard it has no organization for. Consumed here so it fires once.
   */
  const postLoginPath = (): string => {
    if (typeof window === 'undefined') return '/dashboard';
    const pending =
      window.sessionStorage.getItem('biasafe_onboarding_pending') === '1';
    if (!pending) return '/dashboard';
    window.sessionStorage.removeItem('biasafe_onboarding_pending');
    return '/onboarding';
  };

  /**
   * Everything that happens after Identity Platform hands us a token. Shared
   * by password login, Google SSO and the MFA challenge so the three cannot
   * drift — the ordering below is load-bearing and easy to get wrong.
   */
  const finishAccessLogin = (result: AccessSessionResponse) => {
    if (audience === 'advisor') clearPendingSignupCreds();
    queryClient.clear();
    if (!result.selectedAccess) {
      window.location.assign(accessPage(audience));
      return;
    }
    if (result.selectedAccess.audience === 'portal') {
      usePortalSessionStore
        .getState()
        .setAuthenticated(result.selectedAccess.displayName);
      window.location.assign('/portal');
      return;
    }
    setAuthenticated();
    markSignInCompleted();
    window.location.assign(postLoginPath());
  };

  const finishGcpLogin = async (
    idToken: string,
    emailForChallenge?: string,
  ) => {
    try {
      const result = await exchangeAccessSession(idToken, audience);
      finishAccessLogin(result);
    } catch (err) {
      // Our own email factor: Firebase is satisfied, but the backend declined
      // to mint the session cookie until a code is entered. Not an error —
      // the user is mid-sign-in.
      const { isEmailMfaRequired, emailMfaTtlMinutes } =
        await import('../gcp/emailMfa');
      if (isEmailMfaRequired(err)) {
        setMfaChallenge({
          kind: 'email',
          idToken,
          ttlMinutes: emailMfaTtlMinutes(err) ?? undefined,
          // The address they signed in with, which is the one the code went to.
          target: emailForChallenge,
        });
        if (extractErrorData(err)?.codeDelivered === false) {
          setError(
            'The verification email could not be sent. Try sending a new code in a moment.',
          );
        }
        setGcpPending(false);
        return;
      }
      throw err;
    }
  };

  /**
   * If `err` is "a second factor is required", set up the challenge and say so.
   *
   * ONE implementation for every way of signing in: password, Google and
   * Microsoft by popup, and the Google and Microsoft sign-up flows that sign in
   * with an ID token. The password was right (or the provider confirmed the
   * person), so this is a prompt, not a failure, and must not be rendered as
   * one. Returns true when it handled the error, false when the caller should
   * carry on treating it as a failure.
   */
  const resumeWithSecondFactor = async (err: unknown): Promise<boolean> => {
    const gcp = await import('../gcp/gcpAuth');
    if (!(err instanceof gcp.MfaRequiredError)) return false;
    // Read the factor off the hint instead of assuming it.
    //
    // This branch used to hardcode `kind: 'phone'`, on the reasoning that
    // phone was the only Firebase-native factor enrolled. With TOTP back
    // that assumption LOCKS PEOPLE OUT: a TOTP hint would be labelled
    // phone, the challenge screen would try to send an SMS for a factor
    // that has no delivery channel, and the account would be
    // unreachable, by the user who had just followed our own advice to
    // pick the strongest method.
    //
    // Prefer a TOTP hint when the account has several. Its code is
    // already on the user's device, so it is the one that cannot fail to
    // arrive.
    const picked = pickMfaFactor(err.hints);
    if (picked) {
      smsAutoSent.current = false;
      setMfaChallenge({
        ...picked,
        resolver: err.resolver,
        alternatives: alternativeMfaFactors(err.hints, picked.factorUid),
      });
    }
    setGcpPending(false);
    return true;
  };

  /**
   * A Google or Microsoft sign-in found no account for this address.
   *
   * With the provider's sign-up flag on, that is the start of creating one, not
   * an error: show the "Create your account" step. With it off, return false and
   * the caller shows `firebaseAuthMessage`'s "sign up with email or ask for an
   * invitation" wording.
   */
  const offerAccountCreation = async (
    provider: SsoProviderName,
    err: unknown,
  ): Promise<boolean> => {
    if (firebaseErrorCode(err) !== ADMIN_RESTRICTED_CODE) return false;
    if (audience === 'portal') {
      setError(
        'No client access is available with this sign-in. Use the address on your invitation or contact your advisor.',
      );
      setGcpPending(false);
      return true;
    }
    const clientId = await signupClientId(provider);
    if (!clientId) return false;
    setError(null);
    setSsoCreate({ provider, clientId });
    setGcpPending(false);
    return true;
  };

  const cancelSsoCreate = () => {
    setSsoCreate(null);
    setError(null);
    setGcpPending(false);
  };

  const login = (payload: LoginPayload) => {
    setError(null);
    setCredentialsRejected(false);
    setGcpPending(true);
    void (async () => {
      const { signInWithPassword } = await import('../gcp/gcpAuth');
      try {
        // A sign-in failure is now just a sign-in failure. This used to
        // fall back to `/auth/gcp/migrate-login`, which verified the
        // password against the old provider and copied it into Firebase.
        // That endpoint is gone with the provider; anyone who never signed
        // in after the cutover recovers through forgot-password, which
        // creates the Firebase account when our own user table knows the
        // address.
        const idToken = await signInWithPassword(
          payload.email,
          payload.password,
        );
        // The address is passed through so the email challenge can name the
        // inbox it just wrote to. SSO does not have one to give — Firebase
        // holds it, not us — and the screen falls back to saying nothing
        // rather than guessing.
        await finishGcpLogin(idToken, payload.email);
      } catch (err) {
        if (await resumeWithSecondFactor(err)) return;
        // Firebase errors carry `code`, not the API's `response.data.detail`,
        // so extractErrorMessage alone always produced the same generic
        // sentence — which made "wrong password" and "the whole environment
        // cannot authenticate anyone" look identical for hours on 2026-08-17.
        if (isCredentialRejection(err)) {
          // Wrong password and unknown address come back as the SAME code on
          // purpose (email-enumeration protection). One neutral message, with
          // both ways forward, and nothing that says which it was.
          setError(
            audience === 'portal'
              ? 'Check your email and password. Use the address on your client invitation.'
              : NEUTRAL_LOGIN_ERROR,
          );
          setCredentialsRejected(true);
          setGcpPending(false);
          return;
        }
        setError(
          firebaseAuthMessage(
            err,
            extractErrorMessage(err, 'Login failed. Please try again.'),
          ),
        );
        setGcpPending(false);
      }
    })();
  };

  // Google SSO via Identity Platform (GCP auth path only).
  //
  // Failures go through firebaseAuthMessage like the password form. Before,
  // only extractErrorMessage ran, which cannot read a Firebase error, so a
  // blocked popup, an account that already used a password and Google
  // refusing the app all read "Google sign-in failed. Please try again."
  const beginGoogleSSO = () => {
    setError(null);
    setGcpPending(true);
    void (async () => {
      try {
        const { signInWithGoogle } = await import('../gcp/gcpAuth');
        const idToken = await signInWithGoogle();
        await finishGcpLogin(idToken);
      } catch (err) {
        if (await resumeWithSecondFactor(err)) return;
        if (await offerAccountCreation('Google', err)) return;
        reportSsoFailure('Google', err);
        setError(
          firebaseAuthMessage(
            err,
            extractErrorMessage(
              err,
              'Google sign-in failed. Please try again.',
            ),
          ),
        );
        setGcpPending(false);
      }
    })();
  };

  // Microsoft SSO, same Identity Platform path as Google above.
  //
  // The failure most likely to reach a user here is
  // auth/operation-not-allowed: the provider not enabled on the project. The
  // button is gated on config precisely so this should not happen, but a
  // config flag and a provider setting can disagree. firebaseAuthMessage now
  // says the method is not enabled, and the console line names Microsoft and
  // the code, which together make it reportable. Anything Firebase does not
  // explain still falls back to a message that names the provider.
  const beginMicrosoftSSO = () => {
    setError(null);
    setGcpPending(true);
    void (async () => {
      try {
        const { signInWithMicrosoft } = await import('../gcp/gcpAuth');
        const idToken = await signInWithMicrosoft();
        await finishGcpLogin(idToken);
      } catch (err) {
        if (await resumeWithSecondFactor(err)) return;
        if (await offerAccountCreation('Microsoft', err)) return;
        reportSsoFailure('Microsoft', err);
        setError(
          firebaseAuthMessage(
            err,
            extractErrorMessage(
              err,
              'Microsoft sign-in failed. Please try again.',
            ),
          ),
        );
        setGcpPending(false);
      }
    })();
  };

  const beginFirmSSO = (providerId: string) => {
    setError(null);
    setGcpPending(true);
    void (async () => {
      try {
        const { signInWithFirm } = await import('../gcp/gcpAuth');
        await finishGcpLogin(await signInWithFirm(providerId));
      } catch (failure) {
        if (await resumeWithSecondFactor(failure)) return;
        // An enterprise login never falls into public account creation.
        setError(
          firebaseAuthMessage(
            failure,
            extractErrorMessage(
              failure,
              'Could not sign in with your organization. Contact your administrator.',
            ),
          ),
        );
        setGcpPending(false);
      }
    })();
  };

  // "Sign up with Microsoft" for a brand-new address (MICROSOFT_SIGNUP_ENABLED).
  //
  // The popup above both signs in and creates, and creation is disabled on the
  // Identity Platform project, so this takes a Microsoft ID token directly,
  // lets the backend verify it and create the account, and then signs in with
  // that SAME token. Must be called straight from the click: it opens a popup
  // before its first await. See qpm_api/v1/auth/microsoft_signup.py.
  const beginMicrosoftSignup = (clientId: string, acceptedTerms: boolean) => {
    if (!acceptedTerms) {
      setError(
        'Please accept the Terms of Service and Privacy Policy to sign up with Microsoft.',
      );
      return;
    }
    setError(null);
    setGcpPending(true);
    void (async () => {
      let rawNonce = '';
      // True once the backend has answered. From then on a failure is NOT a
      // failed sign-up: the account may well exist (a work account is created
      // active and signed in by the second half of this function), so telling
      // the person to "try again" sends them back to a form for an account
      // they already have. That is what B-CAP saw on 2026-10-20.
      let signupAnswered = false;
      try {
        const { popupMicrosoftIdToken } =
          await import('../gcp/microsoftIdToken');
        const idToken = await popupMicrosoftIdToken({
          clientId,
          getNonce: async () => {
            const issued = await microsoftSignupNonce();
            rawNonce = issued.nonce;
            return issued.token_nonce;
          },
        });
        const created = await microsoftSignup({
          id_token: idToken,
          accepted_terms: true,
        });
        signupAnswered = true;
        // A PERSONAL Microsoft account is created unverified: its address is
        // not trusted until the mailbox owner opens the link we emailed. So it
        // is NOT signed in here (the session route would refuse it anyway);
        // it gets the same "check your email" screen as the email and password
        // sign-up.
        if (created.status === 'verification_required') {
          if (created.user_id) {
            setEmailDelivered(created.email_delivered !== false);
            setUserId(created.user_id);
            setSignupSuccess(true);
            setGcpPending(false);
            navigate({
              to: '/verify-email',
              search: { userId: created.user_id },
              replace: true,
            });
          } else {
            // No user id: nothing was created or emailed because the address
            // already has an account. The reply is otherwise identical for a
            // known and an unknown address, so it stays this vague.
            setError(
              'We could not create a new account for this address. If you already use BIASafe, sign in with your email and password, or choose Forgot password.',
            );
            setGcpPending(false);
          }
          return;
        }
        const { signInWithMicrosoftIdToken } = await import('../gcp/gcpAuth');
        await finishGcpLogin(
          await signInWithMicrosoftIdToken(idToken, rawNonce),
        );
      } catch (err) {
        // An existing account with a second factor is not a failed sign-up:
        // the token was good and the person is who they say. Continue to the
        // second-factor screen.
        if (await resumeWithSecondFactor(err)) return;
        console.warn('Microsoft sign-up failed');
        const code = firebaseErrorCode(err) ?? '';
        setError(
          extractErrorData(err)?.reason === 'microsoft_email_unverified'
            ? 'Microsoft did not confirm your email address, so we cannot create an account from it. Use a work account whose email domain is verified, a personal Microsoft account, or sign up with your email and password.'
            : code === 'auth/account-exists-with-different-credential'
              ? 'This email already signs in another way. Use the method you signed up with, such as your email and password.'
              : firebaseAuthMessage(
                  err,
                  extractErrorMessage(
                    err,
                    signupAnswered
                      ? 'Your Microsoft account was accepted, but we could not finish signing you in. Your account may already be set up: choose Sign in with Microsoft.'
                      : 'Microsoft sign-up failed. Please try again.',
                  ),
                ),
        );
        setGcpPending(false);
      }
    })();
  };

  /**
   * Send the code again, by whichever route this factor uses.
   *
   * SMS goes back through Firebase, which means clearing the verificationId
   * first — sendMfaSms returns early when one exists, since it is also the
   * on-mount send. Email goes back through /auth/gcp/session, the endpoint
   * that mints the code in the first place; the backend hands back the
   * OUTSTANDING code inside its reuse window rather than a new one, so a
   * resend there cannot invalidate the message already in the inbox.
   *
   * TOTP is absent on purpose: the code is generated on the device, so there
   * is nothing to send and the screen offers no button.
   */
  const resendMfaCode = (recaptchaContainerId: string) => {
    if (!mfaChallenge) return;
    if (mfaChallenge.kind === 'phone') {
      // `force`, not "clear the id and call again": the call below runs in
      // THIS render's closure, where `verificationId` is still set, so the
      // old approach hit the already-sent guard and a resend never sent.
      sendMfaSms(recaptchaContainerId, true);
      return;
    }
    if (mfaChallenge.kind === 'email' && mfaChallenge.idToken) {
      setError(null);
      const idToken = mfaChallenge.idToken;
      void (async () => {
        try {
          // A deliberate resend. Re-posting the session was an automatic
          // trigger the backend answers with the outstanding code inside its
          // 120s reuse window, so nothing was sent for the second minute of
          // the 60s cooldown, and any failure was swallowed.
          const { resendEmailChallenge, emailMfaFailureMessage } =
            await import('../gcp/emailMfa');
          try {
            await resendEmailChallenge(idToken);
          } catch (err) {
            setError(
              emailMfaFailureMessage(err) ??
                extractErrorMessage(
                  err,
                  'Could not send a new code. Please try again.',
                ),
            );
          }
        } catch {
          setError('Could not send a new code. Please try again.');
        }
      })();
    }
  };

  /**
   * For the SMS factor Firebase needs a separate "send the message" step
   * before a code exists to type. Called by the challenge screen on mount, and
   * with `force` by Resend.
   */
  const sendMfaSms = (recaptchaContainerId: string, force = false) => {
    if (!mfaChallenge || mfaChallenge.kind !== 'phone') return;
    if (smsInFlight.current) return;
    if (!force && (mfaChallenge.verificationId || smsAutoSent.current)) return;
    smsAutoSent.current = true;
    smsInFlight.current = true;
    setError(null);
    void (async () => {
      try {
        const { startPhoneSignIn } = await import('../gcp/gcpAuth');
        const verificationId = await startPhoneSignIn(
          mfaChallenge.resolver as never,
          mfaChallenge.factorUid!,
          recaptchaContainerId,
        );
        setMfaChallenge((prev) =>
          prev ? { ...prev, verificationId, smsFailed: false } : prev,
        );
      } catch (err) {
        setMfaChallenge((prev) => (prev ? { ...prev, smsFailed: true } : prev));
        setError(
          mfaChallengeError(err)?.message ??
            extractErrorMessage(
              err,
              'Could not send your code. Please try again.',
            ),
        );
      } finally {
        smsInFlight.current = false;
      }
    })();
  };

  /** Use another enrolled factor for this same held-back sign-in. */
  const switchMfaFactor = (factorUid: string) => {
    if (!mfaChallenge?.alternatives) return;
    const next = mfaChallenge.alternatives.find(
      (a) => a.factorUid === factorUid,
    );
    if (!next) return;
    const hints = [
      {
        uid: mfaChallenge.factorUid!,
        factorId:
          mfaChallenge.kind === 'totp'
            ? TotpMultiFactorGenerator.FACTOR_ID
            : 'phone',
        displayName: mfaChallenge.label,
        phoneNumber: mfaChallenge.target,
      },
      ...mfaChallenge.alternatives.map((a) => ({
        uid: a.factorUid,
        factorId:
          a.kind === 'totp' ? TotpMultiFactorGenerator.FACTOR_ID : 'phone',
        displayName: a.label,
        phoneNumber: a.target,
      })),
    ];
    smsAutoSent.current = false;
    setError(null);
    setMfaChallenge({
      kind: next.kind,
      resolver: mfaChallenge.resolver,
      factorUid: next.factorUid,
      label: next.label,
      target: next.target,
      alternatives: alternativeMfaFactors(hints, next.factorUid),
    });
  };

  /** Finish a sign-in that stopped for a second factor. */
  const submitMfaCode = (oneTimeCode: string) => {
    if (!mfaChallenge) return;
    setError(null);
    setGcpPending(true);
    void (async () => {
      try {
        if (mfaChallenge.kind === 'email') {
          const { completeEmailChallenge } = await import('../gcp/emailMfa');
          // This call mints the cookie itself, so there is no second
          // establishGcpSession — going through finishGcpLogin would re-post
          // the token and be challenged all over again.
          const result = await completeEmailChallenge(
            mfaChallenge.idToken!,
            oneTimeCode,
            audience,
          );
          setMfaChallenge(null);
          finishAccessLogin(result);
          return;
        }

        const gcp = await import('../gcp/gcpAuth');
        // TOTP resolves against the FACTOR uid; SMS against the verificationId
        // its send returned. Passing one where the other belongs fails with an
        // opaque provider error, so they are separate calls rather than one
        // with a switched argument.
        const idToken =
          mfaChallenge.kind === 'totp'
            ? await gcp.resolveTotpSignIn(
                mfaChallenge.resolver as never,
                mfaChallenge.factorUid!,
                oneTimeCode,
              )
            : await gcp.resolvePhoneSignIn(
                mfaChallenge.resolver as never,
                mfaChallenge.verificationId!,
                oneTimeCode,
              );
        setMfaChallenge(null);
        await finishGcpLogin(idToken);
      } catch (err) {
        const status = (err as { response?: { status?: number } }).response
          ?.status;
        const { emailMfaFailureMessage } = await import('../gcp/emailMfa');
        // A wrong code is the expected case and must read as retryable, and so
        // must the ones that only need a new code or a moment: an expired SMS,
        // a rate limit, a dropped connection. Each used to end the whole
        // sign-in with "enter your password again". Only a challenge that is
        // actually gone (expired resolver, rejected token) goes back to the
        // password.
        const firebaseOutcome = mfaChallengeError(err);
        const emailMessage =
          mfaChallenge.kind === 'email' ? emailMfaFailureMessage(err) : null;
        if (
          emailMessage &&
          (status === 400 || status === 429 || status === 502)
        ) {
          setError(emailMessage);
        } else if (firebaseOutcome?.retryable) {
          setError(firebaseOutcome.message);
        } else if (status === 400) {
          setError('That code is not right. Check it and try again.');
        } else {
          setMfaChallenge(null);
          setError(
            firebaseOutcome?.message ??
              extractErrorMessage(
                err,
                'Sign-in expired. Please enter your password again.',
              ),
          );
        }
        setGcpPending(false);
      }
    })();
  };

  const cancelMfaChallenge = () => {
    smsAutoSent.current = false;
    setMfaChallenge(null);
    setError(null);
    setGcpPending(false);
  };

  const signUp = (payload: SignUpPayload) => {
    setError(null);
    // BOTH providers now go through the backend /signup.
    //
    // The gcp path used to sign up client-side with the Firebase SDK and jump
    // straight to /onboarding. That skipped everything /signup does, and every
    // one of these was a real reported bug:
    //   - first/last name were never sent, so the account had no name at all
    //     (SignUpPayload carries them; signUpWithPassword only took email +
    //     password). _signup_gcp sets Firebase displayName AND passes the names
    //     into org provisioning.
    //   - the user was pushed into onboarding BEFORE confirming their email;
    //     /signup deliberately does not authenticate — verify, then log in.
    //   - the "check your email" step (/verify-email) never appeared.
    //   - organization provisioning / domain auto-join was skipped entirely.
    //
    // Stash the credentials so the post-verification auto-login can reuse them
    // instead of making the user retype.
    setPendingSignupCreds({
      email: payload.email,
      password: payload.password,
    });
    signupMutation.mutate({ data: payload });
  };

  // --- Resend Verification ---
  const resendMutation = useResendVerification({
    mutation: {
      onSuccess: () => setEmailDelivered(true),
      onError: (err: any) => {
        setEmailDelivered(false);
        setError(
          extractErrorMessage(
            err,
            'We could not send the email. Please contact support@biasafe.ai.',
          ),
        );
      },
    },
  });

  /**
   * Ask the server whether this account's email is confirmed, and continue if
   * so. `silent` is for the automatic re-checks: a "not yet" answer is not
   * reported, and a previous error stays on screen rather than being wiped by
   * a check the user did not ask for.
   */
  const verifyEmail = (code: string, options?: { silent?: boolean }) => {
    if (!userId) return;
    silentVerifyRef.current = options?.silent === true;
    if (!options?.silent) setError(null);
    verifyMutation.mutate({
      data: { code, userId },
    });
  };

  const resendVerification = () => {
    if (!userId) return;
    setError(null);
    resendMutation.mutate({ data: { userId } });
  };

  // --- Cancel Verification ---
  const cancelMutation = useCancelVerification({
    mutation: {
      onSettled: () => {
        setSignupSuccess(false);
        setUserId(null);
        setError(null);
        // Drop the stashed credentials — the user explicitly aborted
        // the signup flow, no auto-login should happen later.
        clearPendingSignupCreds();
        navigate({ to: '/login', replace: true });
      },
    },
  });

  const cancelVerification = () => {
    if (!userId) return;
    cancelMutation.mutate({ data: { userId } });
  };

  return {
    login,
    signUp,
    emailDelivered,
    mfaChallenge,
    submitMfaCode,
    sendMfaSms,
    resendMfaCode,
    switchMfaFactor,
    cancelMfaChallenge,
    beginGoogleSSO,
    beginMicrosoftSSO,
    beginFirmSSO,
    beginMicrosoftSignup,
    resumeWithSecondFactor,
    completeSignIn: finishGcpLogin,
    ssoCreate,
    cancelSsoCreate,
    credentialsRejected,
    verifyEmail,
    resendVerification,
    cancelVerification,
    error,
    userId,
    signupSuccess,
    isLoading: signupMutation.isPending || gcpPending,
    isVerifying: verifyMutation.isPending,
    isResending: resendMutation.isPending,
    setUserIdExternal: setUserId,
    clearError: () => {
      setError(null);
      setCredentialsRejected(false);
    },
  };
}
